import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type TrackSelectionTrigger, useTrackDisabledSelectAll } from '@air/analytics';
import {
  preSelectItemsAction,
  resetSelectedItemsAction,
  selectedItemsCountSelector,
  selectedItemsStateSelector,
  showShiftSelectHighlightsSelector,
} from '@air/redux-selected-items';

import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useSelectShiftSelectedHighlightedItems } from '~/hooks/useSelectShiftSelectedHighlightedItems';
import { useToggleSelectedItem } from '~/hooks/useToggleSelectedItem';
import { setShiftSelectHighlightedGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { useAirStore } from '~/utils/ReduxUtils';

export type SelectionCommitParams = { trigger: TrackSelectionTrigger; reset?: boolean; shiftKey?: boolean };

export type SelectionMouseModifiers = { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean };
export type SelectionGesture = 'replace' | 'toggle' | 'range';

export const modifiersToSelectionGesture = ({
  shiftKey,
  metaKey,
  ctrlKey,
}: SelectionMouseModifiers): SelectionGesture => {
  // shift outranks cmd/ctrl, matching the board's long-standing commit mapping
  if (shiftKey) {
    return 'range';
  }
  if (metaKey || ctrlKey) {
    return 'toggle';
  }

  return 'replace';
};

/**
 * Single source of truth for turning mouse-event modifiers into the selection
 * trigger + reset flag. Shared by every confirm path (per-card `confirmItemSelection`,
 * the stable mouseup owner) and by `preSelectItem`, so the rules live in one place
 * (add e.g. an `altKey` modifier here, not in three files).
 */
export const modifiersToCommitParams = ({
  shiftKey,
  metaKey,
  ctrlKey,
}: SelectionMouseModifiers): SelectionCommitParams => {
  let trigger: TrackSelectionTrigger = 'click';
  let reset = true;

  if (metaKey || ctrlKey) {
    trigger = 'meta+click';
    reset = false;
  }
  if (shiftKey) {
    trigger = 'shift+click';
    reset = false;
  }

  return { trigger, reset, shiftKey };
};

/**
 * Commits the selection for a given item. Extracted from `useMultiselectItem` so the
 * exact same logic runs from both:
 *   1. the per-card confirm (mouseup on the card's own window listener), and
 *   2. the gallery's single stable mouseup owner (`useConfirmPreSelectedItemOnMouseUp`),
 *      which confirms whatever is currently pre-selected even if the card remounted
 *      mid-press and its own listener was torn down.
 *
 * It clears `preSelectedIds` after committing so the two callers are idempotent: whichever
 * fires first commits and clears the pre-selection; the other sees an empty pre-selection
 * (read fresh from the store) and no-ops — no double-commit.
 */
export const useSelectionCommit = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const { toggleSelectedItem } = useToggleSelectedItem();
  const { selectShiftSelectedHighlightedItems } = useSelectShiftSelectedHighlightedItems();

  const commitSelection = useCallback(
    (item: SelectableGalleryItem, { trigger, reset, shiftKey }: SelectionCommitParams) => {
      const state = store.getState();
      const multiselectHighlightIsShowing = showShiftSelectHighlightsSelector(state);

      // Shift+click commits the anchor→item range synchronously from the selectableItems
      // registry already in the store (no dependency on the async highlight-mode flag or
      // the hover effect). Requires an anchor; without one we fall through to a normal select.
      if (shiftKey && selectedItemsStateSelector(state).anchor) {
        dispatch(setShiftSelectHighlightedGalleryItemsAction({ item }));
        selectShiftSelectedHighlightedItems(item, trigger);
      } else if (multiselectHighlightIsShowing) {
        selectShiftSelectedHighlightedItems(item, trigger);
      } else if (reset) {
        if (selectAll) {
          trackDisabledSelectAll({ trigger: 'item-click', totalSelectionCount: selectedItemsCountSelector(state) });
        }
        resetSelectAll();
        dispatch(resetSelectedItemsAction());
        if (trigger !== 'double-click') {
          toggleSelectedItem({ item, trigger });
        }
      } else if (trigger !== 'double-click') {
        toggleSelectedItem({ item, trigger });
      }

      // Clear the pre-selection so a second confirm (per-card vs. stable owner) is a no-op.
      if (selectedItemsStateSelector(store.getState()).preSelectedIds.length) {
        dispatch(preSelectItemsAction({ itemsIds: [] }));
      }
    },
    [
      dispatch,
      store,
      trackDisabledSelectAll,
      selectAll,
      resetSelectAll,
      toggleSelectedItem,
      selectShiftSelectedHighlightedItems,
    ],
  );

  return { commitSelection };
};
